/* ================================================================
   SUPERMALL THEME POLISH — Interaction & Scroll Reveal Engine
   Skills: Emil Kowalski + design-taste-frontend + high-end-visual-design
   ================================================================ */
(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── 1. Scroll Reveal via IntersectionObserver ─────────────
     Emil Kowalski: never window.scroll, always IO.
     Elements with .reveal get .revealed when they enter viewport.
     Grids (.product-grid, .category-grid, .stagger-reveal)
     also get .revealed for CSS stagger delays. ─────────────── */
  function initScrollReveal() {
    if (prefersReduced) return;

    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -48px 0px' }
    );

    /* Explicit reveal targets */
    document.querySelectorAll('.reveal').forEach(function (el) {
      io.observe(el);
    });

    /* Auto-attach reveal to section titles */
    document.querySelectorAll(
      '.section-title, .section-heading, .collection-title, .page-title'
    ).forEach(function (el) {
      if (!el.classList.contains('reveal')) {
        el.classList.add('reveal');
        io.observe(el);
      }
    });

    /* Stagger grids */
    document.querySelectorAll(
      '.product-grid, .category-grid, .stagger-reveal'
    ).forEach(function (el) {
      el.classList.add('stagger-reveal');
      io.observe(el);
    });
  }

  /* ── 2. Image fade-in on load ───────────────────────────────
     Emil Kowalski: no janky image pops. Fade from opacity:0. ── */
  function initImageFade() {
    document.querySelectorAll('img').forEach(function (img) {
      /* Skip tiny icons */
      if (img.width && img.width < 32) return;
      img.classList.add('img-loading');
      if (img.complete && img.naturalWidth > 0) {
        img.classList.remove('img-loading');
        img.classList.add('img-loaded');
      } else {
        img.addEventListener('load', function () {
          img.classList.remove('img-loading');
          img.classList.add('img-loaded');
        }, { once: true });
        img.addEventListener('error', function () {
          img.classList.remove('img-loading');
          img.classList.add('img-loaded');
        }, { once: true });
      }
    });
  }

  /* ── 3. Cart badge pop animation ───────────────────────────
     Observe DOM mutations on the badge number span and
     trigger a spring-style CSS animation. ──────────────────── */
  function initCartBadgePop() {
    var badge = document.querySelector('[data-cart-badge], .cart-count');
    if (!badge) return;
    var observer = new MutationObserver(function () {
      badge.classList.remove('cart-count-pop');
      void badge.offsetWidth; /* force reflow to restart animation */
      badge.classList.add('cart-count-pop');
    });
    observer.observe(badge, { childList: true, characterData: true, subtree: true });
  }

  /* ── 4. Magnetic micro-effect on primary CTAs ──────────────
     high-end-visual-design: "Magnetic Button Hover Physics".
     Only on pointer-capable devices. Strength is subtle (0.12).
     Emil: animate only transform, never layout props. ──────── */
  function initMagneticButtons() {
    if (!canHover || prefersReduced) return;
    document.querySelectorAll('.btn--primary, .atc-btn, .cart-btn').forEach(function (btn) {
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width  / 2) * 0.12;
        var y = (e.clientY - r.top  - r.height / 2) * 0.12;
        btn.style.transform = 'translate(' + x + 'px,' + y + 'px)';
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.transform = '';
      });
      /* Preserve active scale on top of magnetic offset */
      btn.addEventListener('mousedown', function () {
        btn.style.transform = 'scale(0.97)';
      });
      btn.addEventListener('mouseup', function () {
        btn.style.transform = '';
      });
    });
  }

  /* ── 5. Header scroll state ─────────────────────────────────
     Emil: specific transition on shadow, not transition:all.
     Add .scrolled class so CSS polish applies box-shadow. ──── */
  function initHeaderScroll() {
    var header = document.querySelector('.site-header');
    if (!header) return;
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          header.classList.toggle('scrolled', window.scrollY > 8);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ── 6. Tooltip: skip animation delay after first open ──────
     Emil: instant tooltips after the first one is shown.
     We use a simple class-based flag. ──────────────────────── */
  function initTooltipInstant() {
    document.querySelectorAll('[data-tooltip]').forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        document.querySelectorAll('[data-tooltip]').forEach(function (other) {
          if (other !== el) other.setAttribute('data-instant', '');
        });
      });
      el.addEventListener('mouseleave', function () {
        document.querySelectorAll('[data-tooltip]').forEach(function (other) {
          other.removeAttribute('data-instant');
        });
      });
    });
  }

  /* ── 7. Mobile nav tap feedback ─────────────────────────────
     Immediate visual response. Emil: button must feel
     responsive to press. Uses CSS .mob-nav-item:active. ────── */
  /* Handled fully via CSS :active in theme-polish.css */

  /* ── 8. Staggered mobile drawer nav links ───────────────────
     high-end-visual-design: 'Staggered Mask Reveal'.
     When drawer opens, stagger the nav link opacity. ────────── */
  function initDrawerStagger() {
    if (prefersReduced) return;
    var drawerTrigger = document.querySelector('[data-drawer-open], .header-hamburger');
    var drawer = document.querySelector('.mobile-drawer');
    if (!drawerTrigger || !drawer) return;

    var links = drawer.querySelectorAll('a, button');
    /* Set initial state */
    links.forEach(function (link) {
      link.style.opacity = '0';
      link.style.transform = 'translateY(12px)';
      link.style.transition = 'opacity 300ms var(--ease-out), transform 300ms var(--ease-out)';
    });

    /* Watch for .open class being added via MutationObserver */
    var drawerObserver = new MutationObserver(function () {
      if (drawer.classList.contains('open')) {
        links.forEach(function (link, i) {
          link.style.transitionDelay = (i * 45) + 'ms';
          link.style.opacity = '1';
          link.style.transform = 'translateY(0)';
        });
      } else {
        links.forEach(function (link) {
          link.style.transitionDelay = '0ms';
          link.style.opacity = '0';
          link.style.transform = 'translateY(12px)';
        });
      }
    });
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ── Init ─────────────────────────────────────────────────── */
  function init() {
    initScrollReveal();
    initImageFade();
    initCartBadgePop();
    initMagneticButtons();
    initHeaderScroll();
    initTooltipInstant();
    initDrawerStagger();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
